import { ServiceAvailabilityInfo } from './interfaces/waitlist.interfaces';

export interface ShortenedDurationState {
  hasShortenedDuration: boolean;
  requiresConfirmation: boolean;
  info: { remainingMinutes: number; durationPercentage: number } | null;
  adjustedDurationMs: number | null;
}

const MS_PER_MIN = 60000;
const CONFIRMATION_THRESHOLD = 50;

/**
 * Calculate shortened duration state based on selected services
 */
export function calculateShortenedDurationState(
  servicesInDropdown: ServiceAvailabilityInfo[],
  selectedServiceIds: string[],
): ShortenedDurationState {
  if (!selectedServiceIds?.length) {
    return {
      hasShortenedDuration: false,
      requiresConfirmation: false,
      info: null,
      adjustedDurationMs: null,
    };
  }

  const selectedServices = servicesInDropdown.filter(s => selectedServiceIds.includes(s._id));

  let minRemainingTime = Infinity;
  let minDurationPercentage = 100;
  let anyShortened = false;

  for (const service of selectedServices) {
    if (service.hasFullDuration === false && service.remainingTimeMs !== undefined) {
      anyShortened = true;
      if (service.remainingTimeMs < minRemainingTime) {
        minRemainingTime = service.remainingTimeMs;
        minDurationPercentage = service.durationPercentage || 0;
      }
    }
  }

  if (!anyShortened) {
    return {
      hasShortenedDuration: false,
      requiresConfirmation: false,
      info: null,
      adjustedDurationMs: null,
    };
  }

  return {
    hasShortenedDuration: true,
    requiresConfirmation: minDurationPercentage < CONFIRMATION_THRESHOLD,
    info: {
      remainingMinutes: Math.round(minRemainingTime / MS_PER_MIN),
      durationPercentage: minDurationPercentage,
    },
    adjustedDurationMs: minRemainingTime !== Infinity ? minRemainingTime : null,
  };
}

/**
 * Get service option display name with shortened duration warning
 */
export function getServiceOptionNameWithWarning(service: ServiceAvailabilityInfo): string {
  let name = service.name;
  if (service.hasFullDuration === false && service.remainingTimeMs !== undefined) {
    const remainingMinutes = Math.round(service.remainingTimeMs / MS_PER_MIN);
    name = `⚠️ ${name} (${remainingMinutes} min avail)`;
  }
  return name;
}
